import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MessageCircle, Menu, X, ChevronDown, ArrowUpRight } from "lucide-react";
import { SITE } from "@/data/site";
import { APPLICATIONS } from "@/data/applications";
import { MATERIALS } from "@/data/materials";
import { PRODUCTS } from "@/data/products";

const DropMenu = ({ label, items, base, testid }) => (
  <div className="relative group" data-testid={testid}>
    <button
      type="button"
      className="flex items-center gap-1 px-4 py-6 text-[13px] font-semibold uppercase tracking-[0.14em] text-[#706F70] group-hover:text-[#334772] transition-colors duration-200"
    >
      {label}
      <ChevronDown className="w-3.5 h-3.5 transition-transform duration-200 group-hover:rotate-180" />
    </button>
    <div className="invisible opacity-0 translate-y-2 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 absolute left-0 top-full w-[340px] bg-white border border-[#334772]/15 shadow-[0_20px_40px_-10px_rgba(51,71,114,0.25)]">
      <div className="h-[2px] w-full bg-[#334772]" />
      <ul
        className="py-2 max-h-[60vh] overflow-y-auto overscroll-contain"
        data-lenis-prevent
        onWheel={(e) => e.stopPropagation()}
      >
        {items.map((item) => (
          <li key={item.slug}>
            <Link
              to={`${base}/${item.slug}`}
              className="flex items-center justify-between px-5 py-2.5 text-sm text-[#706F70] hover:text-[#334772] hover:bg-[#334772]/5 transition-colors duration-150"
            >
              {item.nav || item.name}
              <ArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-40" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const Nav = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const close = () => setOpen(false);

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-white/85 backdrop-blur-xl border-b border-[#334772]/15">
      <div className="hidden md:flex items-center justify-between px-6 lg:px-10 h-8 bg-[#334772] text-white font-mono-spec text-[11px] tracking-widest uppercase">
        <span data-testid="topbar-spec">[ ISO 9001:2015 CERTIFIED · SINCE 1969 ]</span>
        <div className="flex items-center gap-6">
          <a href={SITE.phone1Href} className="hover:underline" data-testid="topbar-call">{SITE.phone1}</a>
          <a href={SITE.mainSite} target="_blank" rel="noopener noreferrer" className="hover:underline" data-testid="topbar-main-site">
            mescosprings.com
          </a>
        </div>
      </div>
      <nav className="flex items-center justify-between px-6 lg:px-10">
        <Link to="/" className="flex items-center gap-3 py-2" data-testid="nav-logo" onClick={close}>
          <img src="/mesco-logo.png" alt="MESCO Springs — Absorb. Restore. Energise." className="h-12 w-auto" />
        </Link>
        <div className="hidden lg:flex items-center">
          <NavLink to="/" className="px-4 py-6 text-[13px] font-semibold uppercase tracking-[0.14em] text-[#706F70] hover:text-[#334772] transition-colors duration-200" data-testid="nav-home">
            Home
          </NavLink>
          <DropMenu label="Applications" items={APPLICATIONS} base="/applications" testid="nav-applications" />
          <DropMenu label="Materials" items={MATERIALS} base="/materials" testid="nav-materials" />
          <DropMenu label="Products" items={PRODUCTS} base="/products" testid="nav-products" />
          <NavLink to="/capability" className="px-4 py-6 text-[13px] font-semibold uppercase tracking-[0.14em] text-[#706F70] hover:text-[#334772] transition-colors duration-200" data-testid="nav-capability">
            Capability
          </NavLink>
          <NavLink to="/journal" className="px-4 py-6 text-[13px] font-semibold uppercase tracking-[0.14em] text-[#706F70] hover:text-[#334772] transition-colors duration-200" data-testid="nav-journal">
            Journal
          </NavLink>
          <Link
            to="/enquiry"
            data-testid="nav-enquiry"
            className="ml-4 px-6 py-3 bg-[#334772] text-white text-[13px] font-bold uppercase tracking-[0.14em] hover:bg-[#2A3C60] transition-colors duration-200"
          >
            Send RFQ
          </Link>
        </div>
        <button className="lg:hidden text-[#334772] p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden bg-white border-t border-[#334772]/15"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-4 flex flex-col gap-1 max-h-[70vh] overflow-y-auto" data-lenis-prevent>
              {[
                { to: "/", label: "Home" },
                { to: "/applications", label: "Applications" },
                { to: "/materials", label: "Materials" },
                { to: "/products", label: "Products" },
                { to: "/capability", label: "Capability" },
                { to: "/journal", label: "Journal" },
                { to: "/enquiry", label: "Send RFQ" },
              ].map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={close}
                  className={`py-3 border-b border-[#334772]/10 font-display text-2xl tracking-wide ${location.pathname === l.to ? "text-[#334772]" : "text-[#706F70]"}`}
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

const Footer = () => (
  <footer className="bg-[#334772] border-t border-[#334772]" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
      <div>
        <span className="font-display text-4xl text-white tracking-wide">MESCO</span>
        <p className="mt-1 font-mono-spec text-[10px] tracking-[0.3em] text-[#9C9C9C] uppercase">{SITE.tagline}</p>
        <p className="mt-5 text-sm text-[#DDE3EE] leading-relaxed">
          {SITE.fullName} — ISO 9001:2015 certified manufacturer of precision and heavy duty springs, 0.10 mm to 40.00 mm wire, since 1969.
        </p>
        <p className="mt-4 text-xs text-[#9C9C9C] leading-relaxed" data-testid="footer-address">{SITE.address}</p>
      </div>
      <div>
        <h4 className="font-mono-spec text-[11px] tracking-[0.25em] uppercase text-[#9C9C9C] mb-5">Applications</h4>
        <ul className="space-y-2">
          {APPLICATIONS.map((a) => (
            <li key={a.slug}>
              <Link to={`/applications/${a.slug}`} className="text-sm text-[#DDE3EE] hover:text-white transition-colors duration-150">
                {a.nav}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="font-mono-spec text-[11px] tracking-[0.25em] uppercase text-[#9C9C9C] mb-5">Materials</h4>
        <ul className="space-y-2">
          {MATERIALS.map((m) => (
            <li key={m.slug}>
              <Link to={`/materials/${m.slug}`} className="text-sm text-[#DDE3EE] hover:text-white transition-colors duration-150">
                {m.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="font-mono-spec text-[11px] tracking-[0.25em] uppercase text-[#9C9C9C] mb-5">Contact</h4>
        <ul className="space-y-3 text-sm">
          <li><a href={SITE.phone1Href} className="text-[#DDE3EE] hover:text-white transition-colors" data-testid="footer-phone">{SITE.phone1}</a></li>
          <li><a href={SITE.phone2Href} className="text-[#DDE3EE] hover:text-white transition-colors">{SITE.phone2}</a></li>
          <li><a href={`mailto:${SITE.email}`} className="text-[#DDE3EE] hover:text-white transition-colors" data-testid="footer-email">{SITE.email}</a></li>
          <li>
            <a href={SITE.mainSite} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-white hover:text-[#9C9C9C] transition-colors font-semibold" data-testid="footer-main-site">
              www.mescosprings.com <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </li>
        </ul>
        <h4 className="font-mono-spec text-[11px] tracking-[0.25em] uppercase text-[#6B7280] mt-8 mb-4">Products</h4>
        <ul className="space-y-2">
          {PRODUCTS.map((p) => (
            <li key={p.slug}>
              <Link to={`/products/${p.slug}`} className="text-sm text-[#DDE3EE] hover:text-white transition-colors duration-150">
                {p.nav}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
    <div className="border-t border-white/15">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
        <span className="font-mono-spec text-[10px] tracking-widest text-[#9C9C9C] uppercase">© {new Date().getFullYear()} MESCO Springs · Mumbai, India</span>
        <span className="font-mono-spec text-[10px] tracking-widest text-[#9C9C9C] uppercase">Absorb · Restore · Energize</span>
      </div>
    </div>
  </footer>
);

const StickyCTA = () => (
  <div className="fixed bottom-5 right-5 z-50 flex flex-col gap-3" data-testid="sticky-cta-bar">
    <a
      href={SITE.whatsapp}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="WhatsApp MESCO Springs"
      data-testid="whatsapp-cta"
      className="whatsapp-pulse w-14 h-14 flex items-center justify-center bg-[#25D366] text-white shadow-[0_10px_30px_rgba(0,0,0,0.5)] hover:scale-105 transition-transform duration-200"
    >
      <MessageCircle className="w-6 h-6" />
    </a>
    <a
      href={SITE.phone1Href}
      aria-label="Call MESCO Springs"
      data-testid="call-cta"
      className="w-14 h-14 flex items-center justify-center bg-[#334772] text-white shadow-[0_10px_30px_rgba(51,71,114,0.35)] hover:bg-[#2A3C60] hover:scale-105 transition-all duration-200"
    >
      <Phone className="w-6 h-6" />
    </a>
  </div>
);

export default function Layout({ children }) {
  return (
    <div className="min-h-screen bg-[#FEFEFE]">
      <Nav />
      <main>{children}</main>
      <Footer />
      <StickyCTA />
    </div>
  );
}
